import type { TiebreakerTeam, TiebreakerStepFn, StepResult, GameResult } from '../types';
import { RankMethod } from '../../enums/rank-method';
import { computeHthRecords, hthWinPct, addExplanation, addExplanationAll } from '../helpers';

/**
 * Create an HTH win-percentage step.
 * Computes pairwise H2H records (optionally filtered), groups by aggregate win%.
 * - All distinct → full break
 * - Top group smaller than input → partial break (top group wins)
 * - Single group → no break
 */
export function createH2hPctStep<T extends TiebreakerTeam>(
  gameFilter?: (game: GameResult) => boolean,
  rankMethod: RankMethod = RankMethod.HTH,
  detailKey: 'HTH' | 'DivHTH' = 'HTH',
): TiebreakerStepFn<T> {
  return (teams: T[], ctx): StepResult<T> => {
    const hthRecords = computeHthRecords(teams, ctx.games, gameFilter);
    addHthDetails(teams, ctx, hthRecords, detailKey);

    const teamsWithPct = teams.map(t => ({
      team: t,
      pct: hthWinPct(hthRecords, t.teamAbbreviation),
    }));

    teamsWithPct.sort((a, b) => b.pct - a.pct);

    // Group by HTH pct
    const groups: { teams: T[]; pct: number }[] = [];
    let currentPct = teamsWithPct[0].pct;
    let currentGroup: T[] = [teamsWithPct[0].team];
    for (let i = 1; i < teamsWithPct.length; i++) {
      if (teamsWithPct[i].pct === currentPct) {
        currentGroup.push(teamsWithPct[i].team);
      } else {
        groups.push({ teams: currentGroup, pct: currentPct });
        currentGroup = [teamsWithPct[i].team];
        currentPct = teamsWithPct[i].pct;
      }
    }
    groups.push({ teams: currentGroup, pct: currentPct });

    if (groups.length === 1) {
      // No break: all teams have same HTH pct
      addExplanationAll(ctx, teams, `HTH tied at ${(currentPct * 100).toFixed(1)}%.`);
      return { winners: [], remaining: teams };
    }

    if (groups.length === teams.length) {
      // Full break: all distinct
      const ordered = groups.map(g => g.teams[0]);
      for (const t of ordered) {
        addExplanation(ctx, t, `HTH: ${(teamsWithPct.find(tp => tp.team === t)!.pct * 100).toFixed(1)}%.`);
      }
      return { winners: ordered, remaining: [], rankMethod };
    }

    // Partial break: top group wins
    const winners = groups[0].teams;
    const remaining = groups.slice(1).flatMap(g => g.teams);
    for (const t of winners) {
      addExplanation(ctx, t, `HTH winner: ${(groups[0].pct * 100).toFixed(1)}%.`);
    }
    return { winners, remaining, rankMethod };
  };
}

/** Default HTH step (no game filter) */
export const h2hPctStep = createH2hPctStep<TiebreakerTeam>();

function addHthDetails<T extends TiebreakerTeam>(
  teams: T[],
  ctx: Parameters<TiebreakerStepFn<T>>[1],
  hthRecords: Map<string, Map<string, number[]>>,
  detailKey: 'HTH' | 'DivHTH',
): void {
  for (const team of teams) {
    const rows: string[] = [];
    const opponents = hthRecords.get(team.teamAbbreviation);
    if (!opponents) {
      continue;
    }

    for (const opponent of teams) {
      if (opponent.teamAbbreviation === team.teamAbbreviation) {
        continue;
      }
      const record = opponents.get(opponent.teamAbbreviation);
      if (!record) {
        continue;
      }
      const [wins, losses, ties] = record;
      if (wins + losses + ties === 0) {
        continue;
      }
      rows.push(`${opponent.teamAbbreviation} (${wins}-${losses}${ties ? `-${ties}` : ''})`);
    }

    if (rows.length > 0) {
      const existing = ctx.explainerDetails.get(team.teamAbbreviation) ?? {};
      const currentRows = existing[detailKey] ?? [];
      ctx.explainerDetails.set(team.teamAbbreviation, {
        ...existing,
        [detailKey]: [...currentRows, ...rows],
      });
    }
  }
}
